'use client';

import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const EASE_REVEAL = [0.22, 1, 0.36, 1] as const;

const backdropVariants: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: { duration: 0.22, ease: 'easeOut' },
	},
	exit: {
		opacity: 0,
		transition: { duration: 0.2, ease: 'easeIn', delay: 0.12 },
	},
};

const panelVariants: Variants = {
	hidden: {
		clipPath: 'inset(0% 0% 100% 0%)',
	},
	visible: {
		clipPath: 'inset(0% 0% 0% 0%)',
		transition: {
			duration: 0.52,
			ease: EASE_REVEAL,
			delay: 0.12,
		},
	},
	exit: {
		clipPath: 'inset(0% 0% 100% 0%)',
		transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
	},
};

export const navDropdownContentVariants: Variants = {
	hidden: {},
	visible: {
		transition: {
			staggerChildren: 0.045,
			delayChildren: 0.28,
		},
	},
	exit: {
		transition: {
			staggerChildren: 0.02,
			staggerDirection: -1,
		},
	},
};

export const navDropdownItemVariants: Variants = {
	hidden: { opacity: 0, y: 16 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.36, ease: EASE_REVEAL },
	},
	exit: {
		opacity: 0,
		y: 8,
		transition: { duration: 0.12 },
	},
};

type NavDropdownPanelProps = {
	isOpen: boolean;
	menuId: string;
	onClose: () => void;
	onPanelMouseEnter?: () => void;
	onPanelMouseLeave?: () => void;
	children: ReactNode;
};

export function NavDropdownPanel({
	isOpen,
	menuId,
	onClose,
	onPanelMouseEnter,
	onPanelMouseLeave,
	children,
}: NavDropdownPanelProps) {
	const prefersReducedMotion = useReducedMotion();
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted) return null;

	return createPortal(
		<AnimatePresence>
			{isOpen && (
				<>
					{/* Portaled so header transform does not trap fixed positioning */}
					<motion.div
						key={`${menuId}-backdrop`}
						className="fixed inset-0 top-20 z-40 bg-[#0A0A0A]/45 cursor-default"
						variants={prefersReducedMotion ? undefined : backdropVariants}
						initial={prefersReducedMotion ? false : 'hidden'}
						animate={prefersReducedMotion ? undefined : 'visible'}
						exit={prefersReducedMotion ? undefined : 'exit'}
						aria-hidden
						onMouseEnter={onClose}
						onClick={onClose}
					/>
					<motion.div
						key={menuId}
						id={menuId}
						role="menu"
						className="fixed left-0 top-20 z-[49] w-screen"
						initial={false}
						onMouseEnter={onPanelMouseEnter}
						onMouseLeave={onPanelMouseLeave}
					>
						<motion.div
							className="-mt-px relative overflow-hidden bg-white border-b border-[#F5F5F5] shadow-xl"
							variants={prefersReducedMotion ? undefined : panelVariants}
							initial={prefersReducedMotion ? false : 'hidden'}
							animate={prefersReducedMotion ? undefined : 'visible'}
							exit={prefersReducedMotion ? undefined : 'exit'}
							style={{ transformOrigin: 'top center' }}
						>
							<motion.div
								variants={
									prefersReducedMotion ? undefined : navDropdownContentVariants
								}
								initial={prefersReducedMotion ? false : 'hidden'}
								animate={prefersReducedMotion ? undefined : 'visible'}
								exit={prefersReducedMotion ? undefined : 'exit'}
							>
								{children}
							</motion.div>
						</motion.div>
					</motion.div>
				</>
			)}
		</AnimatePresence>,
		document.body
	);
}
